import React, { useEffect, useRef } from "react";

const BLOBS = [
  { color: [211, 102, 103], x: 0.15, y: 0.1, r: 0.35 },   // coral
  { color: [167, 197, 119], x: 0.8, y: 0.5, r: 0.32 },    // green
  { color: [127, 151, 194], x: 0.4, y: 0.85, r: 0.4 },    // blue
  { color: [202, 135, 85], x: 0.75, y: 0.15, r: 0.3 },    // orange
  { color: [198, 201, 105], x: 0.15, y: 0.75, r: 0.34 },  // yellow
];

const BG_COLOR = [200, 194, 186]; // #c8c2ba

// Party mode (fired by holding the logo, see Logo/logoParty.ts): the blobs become the
// dance-floor lights — vivid, circling, swapping colours every bar and pulsing on the beat.
export const PARTY_EVENT = "rank5:party";
export interface PartyDetail {
  bpm: number;
  intensity: number; // 0..1
  playing: boolean; // true starts the party, false winds it down
}
const PARTY_COLORS = [
  [232, 81, 81],
  [147, 219, 104],
  [97, 138, 214],
  [227, 125, 71],
  [255, 216, 74],
];
interface PartyFrame {
  t: number; // seconds into the party
  beats: number;
  amount: number; // 0..1 envelope × intensity
}

const mix = (a: number, b: number, t: number) => a + (b - a) * t;

function renderBackground(canvas: HTMLCanvasElement, party?: PartyFrame) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Fill base color
  ctx.fillStyle = `rgb(${BG_COLOR[0]},${BG_COLOR[1]},${BG_COLOR[2]})`;
  ctx.fillRect(0, 0, w, h);

  // Draw each blob as a radial gradient
  BLOBS.forEach((blob, i) => {
    let { x, y, r: size } = blob;
    let [r, g, b] = blob.color;
    let alpha = 0.65;
    if (party) {
      const a = party.amount;
      const beat = Math.exp(-(party.beats % 1) * 4);
      // circle the dance floor, swinging a little on every beat
      const angle = party.t * 0.8 + (i * Math.PI * 2) / BLOBS.length + 0.18 * Math.sin(Math.PI * party.beats);
      x = mix(x, 0.5 + 0.34 * Math.cos(angle), a * 0.8);
      y = mix(y, 0.5 + 0.34 * Math.sin(angle), a * 0.8);
      size *= 1 + a * (0.08 + 0.22 * beat);
      // every bar each blob glides into the next colour
      const bar = party.beats / 4;
      const from = PARTY_COLORS[(i + Math.floor(bar)) % PARTY_COLORS.length];
      const to = PARTY_COLORS[(i + Math.floor(bar) + 1) % PARTY_COLORS.length];
      const k = Math.min(1, (bar % 1) * 4);
      r = mix(r, mix(from[0], to[0], k), a);
      g = mix(g, mix(from[1], to[1], k), a);
      b = mix(b, mix(from[2], to[2], k), a);
      alpha = mix(alpha, 0.75 + 0.25 * beat, a);
    }
    const cx = x * w;
    const cy = y * h;
    const radius = size * Math.max(w, h);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    grad.addColorStop(0, `rgba(${r},${g},${b},${alpha})`);
    grad.addColorStop(0.7, `rgba(${r},${g},${b},0.1)`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  });
}

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    renderBackground(canvas);
    const onResize = () => renderBackground(canvas);
    window.addEventListener("resize", onResize);

    // The party runs until a `playing: false` event, then fades back to the calm background.
    let raf = 0;
    let playing = false;
    let bpm = 120;
    let intensity = 0;
    let envelope = 0;
    let t = 0;
    let last = 0;
    const frame = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      t += dt;
      envelope = playing ? Math.min(1, envelope + dt / 0.4) : Math.max(0, envelope - dt / 1.2);
      if (!playing && envelope === 0) {
        raf = 0;
        renderBackground(canvas);
        return;
      }
      renderBackground(canvas, { t, beats: (t * bpm) / 60, amount: envelope * intensity });
      raf = requestAnimationFrame(frame);
    };
    const onParty = (e: Event) => {
      const detail = (e as CustomEvent<PartyDetail>).detail;
      playing = detail.playing;
      if (!detail.playing) return;
      bpm = detail.bpm;
      intensity = detail.intensity;
      if (!raf) {
        t = 0;
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    window.addEventListener(PARTY_EVENT, onParty);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener(PARTY_EVENT, onParty);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: -1,
        pointerEvents: "none",
      }}
    />
  );
};

export default AnimatedBackground;
